import { useEffect } from 'react';
import { toast } from 'sonner';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { logger } from '@/lib/logger';

export const REFRESH_INTERVAL_MILLISECONDS = 60 * 60 * 1000; // 1 hour

export function PWABadge() {
    const {
        offlineReady: [offlineReady],
        needRefresh: [needRefresh, setNeedRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW(swUrl, registration) {
            if (REFRESH_INTERVAL_MILLISECONDS <= 0)
                return;
            if (registration?.active?.state === 'activated') {
                registerPeriodicSync(
                    REFRESH_INTERVAL_MILLISECONDS,
                    swUrl,
                    registration,
                );
            }
            else if (registration?.installing) {
                registration.installing.addEventListener(
                    'statechange',
                    (event) => {
                        const sw = event.target as ServiceWorker;
                        if (sw.state === 'activated') {
                            registerPeriodicSync(
                                REFRESH_INTERVAL_MILLISECONDS,
                                swUrl,
                                registration,
                            );
                        }
                    },
                );
            }
        },
    });

    useEffect(() => {
        if (offlineReady) {
            logger.info('[PWA] App is ready to work offline');
            toast.info('Elektra app is klaar om offline te werken');
        }
        else {
            logger.info('[PWA] App is not ready to work offline');
        }
    }, [offlineReady]);

    useEffect(() => {
        if (needRefresh) {
            logger.info('[PWA] New version available');
            toast.info('Nieuwe versie beschikbaar', {
                duration: Infinity,
                action: {
                    label: 'Verversen',
                    onClick: () => updateServiceWorker(),
                },
                onDismiss: () => setNeedRefresh(false),
            });
        }
        else {
            logger.info('[PWA] No new version available');
        }
    }, [needRefresh, setNeedRefresh, updateServiceWorker]);

    return null;
}

/**
 * This function will register a periodic sync check every hour, you can modify the interval as needed.
 */
function registerPeriodicSync(
    period: number,
    swUrl: string,
    r: ServiceWorkerRegistration,
) {
    if (period <= 0)
        return;

    setInterval(async () => {
        if ('onLine' in navigator && !navigator.onLine)
            return;

        const resp = await fetch(swUrl, {
            cache: 'no-store',
            headers: {
                'cache': 'no-store',
                'cache-control': 'no-cache',
            },
        });

        if (resp?.status === 200)
            await r.update();
    }, period);
}
